import { camelCase, capitalize } from 'lodash';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import getRenderable from '../../../../lib/getRenderable';
import {
    POSITIONS,
    POSITIONS_OPPOSITES,
    UNSUPPORTED_POSITION_ERROR,
    TRIGGERS
} from '../constants';
import {
    BOX_DISTANCE,
    MUTATION_OBSERVER_CONFIG
} from './constants';
import isOverflowing from './isOverflowing';
import classes from './index.module.scss';

/**
 * Returns first scrollable parent
 * @param {HTMLElement} node
 * @returns {HTMLElement}
 */
const getScrollParent = (node) => {
    if (!node) {
        return null;
    }

    if (node.scrollHeight > node.clientHeight) {
        return node;
    } else {
        return getScrollParent(node.parentNode);
    }
};

/**
 * Calculates the max available width for the box to open
 * @returns {Number}
 */
const calculateMaxAvailableWidth = () => window.innerWidth - BOX_DISTANCE * 2;

/**
 * Calculates the box's base positioning attribute (top for vertical positions, left for horizontal positions).
 * @param {String} options.position The box's base position.
 * @param {DOMRect} options.anchorDimensions The box's anchor dimensions.
 * @param {DOMRect} options.boxDimensions The anchor's dimensions.
 * @return {Number}
 */
const calculateBasePositionDistance = ({
    position,
    anchorDimensions,
    boxDimensions
}) => {
    switch (position) {
        case POSITIONS.LEFT:
            return anchorDimensions.left - boxDimensions.width;
        case POSITIONS.RIGHT:
            return anchorDimensions.left + anchorDimensions.width;
        case POSITIONS.TOP:
            return anchorDimensions.top - boxDimensions.height;
        case POSITIONS.BOTTOM:
            return anchorDimensions.bottom;
        default:
            break;
    }
};

const getPaddingAttribute = (position) => camelCase(['padding', capitalize(POSITIONS_OPPOSITES[position])].join(''));

/**
 * Calculates the box's position and returns a style object to be applied to the box element.
 * @param {String} options.position The box's position.
 * @param {HTMLElement} options.box The box element.
 * @param {HTMLElement} options.anchor The anchor element.
 * @param {Boolean} options.allowOverflow Whether the box is allowed to overflow the screen or not.
 * @param {Boolean} options.fullWidth Whether the box should take the full screen width or not.
 * @param {Boolean} options.fitContent Whether the box should take at least the width of the anchor.
 * @return {{top: Number, left: Number}}
 */
const calculatePosition = ({
    position,
    box,
    anchor,
    allowOverflow,
    fullWidth,
    boxFitContent,
    appendToBody
}) => {
    if (!box || !anchor) { return {}; }

    const anchorDimensionsFromViewport = anchor.getBoundingClientRect();
    let anchorDimensions;

    if (appendToBody) {
        anchorDimensions = anchorDimensionsFromViewport;
    } else {
        anchorDimensions = {
            top: anchor.offsetTop,
            bottom: anchor.offsetTop + anchorDimensionsFromViewport.height,
            left: anchor.offsetLeft,
            right: anchor.offsetLeft + anchorDimensionsFromViewport.width,
            width: anchorDimensionsFromViewport.width,
            height: anchorDimensionsFromViewport.height
        };
    }

    const boxDimensions = box.getBoundingClientRect();

    const {
        top: anchorTop,
        bottom: anchorBottom,
        left: anchorLeft,
        right: anchorRight,
        width: anchorWidth,
        height: anchorHeight
    } = anchorDimensions;

    const {
        width: boxWidth,
        height: boxHeight
    } = boxDimensions;

    const verticalMiddleDistance = anchorTop + anchorHeight / 2 - boxHeight / 2;
    const horizontalMiddleDistance = anchorLeft + anchorWidth / 2 - boxWidth / 2;
    let [basePosition, modifier] = position.split('-'); // eslint-disable-line prefer-const

    let left, top;

    const overflowing = !allowOverflow && isOverflowing({
        boxDimensions,
        anchorDimensions: anchorDimensionsFromViewport,
        position: basePosition
    });

    if (overflowing) {
        // Reverse the box's position.
        basePosition = POSITIONS_OPPOSITES[basePosition];
    }

    const padding = getPaddingAttribute(basePosition);

    const basePositionDistance = calculateBasePositionDistance({
        anchorDimensions,
        boxDimensions,
        position: basePosition
    });

    switch (basePosition) {
        case POSITIONS.LEFT:
            top = verticalMiddleDistance;
            left = basePositionDistance;
            break;
        case POSITIONS.RIGHT:
            top = verticalMiddleDistance;
            left = basePositionDistance;
            break;
        case POSITIONS.TOP:
            top = basePositionDistance;
            left = horizontalMiddleDistance;
            break;
        case POSITIONS.BOTTOM:
            top = basePositionDistance;
            left = horizontalMiddleDistance;
            break;
        default:
            break;
    }

    switch (modifier) {
        case POSITIONS.LEFT:
            left = anchorLeft;
            break;
        case POSITIONS.RIGHT:
            left = anchorRight - boxWidth;
            break;
        case POSITIONS.TOP:
            top = anchorTop;
            break;
        case POSITIONS.BOTTOM:
            top = anchorBottom - boxHeight;
            break;
        default:
            break;
    }

    if (fullWidth) {
        left = BOX_DISTANCE;
    }

    const calculatedLeft = appendToBody ? `${window.scrollX + left}px` : `${left}px`;
    const calculatedTop = appendToBody ? `${window.scrollY + top}px` : `${top}px`;

    return {
        style: {
            ...(fullWidth && { width: `${calculateMaxAvailableWidth()}px` }),
            ...(boxFitContent && { minWidth: `${anchorDimensions.width}px` }),
            left: calculatedLeft,
            top: calculatedTop,
            [padding]: `${BOX_DISTANCE}px`
        },
        position: [basePosition, modifier].filter(Boolean).join('-')
    };
};

const Box = ({
    children,
    position,
    container,
    anchor,
    className,
    contentClassName,
    allowOverflow = false,
    fullWidth = false,
    boxFitContent = true,
    isOpen = false,
    style = {},
    onMouseLeave = Function.prototype,
    onMouseEnter = Function.prototype,
    preventCloseAction = Function.prototype,
    appendToBody = true,
    trigger = TRIGGERS.CLICK,
    withAnimation,
    adjustPositionAfterMillis = 0
}) => {
    const [ref, setRef] = useState(null);
    const [mounted, setMounted] = useState(false);
    const [modifiedPosition, setModifiedPosition] = useState(position);

    const callback = () => {

        if (!isOpen) { return; }

        const positionAttributes = calculatePosition({
            position,
            anchor,
            box: ref,
            allowOverflow,
            fullWidth,
            boxFitContent,
            appendToBody
        });

        setModifiedPosition(positionAttributes.position);

        Object.assign(ref.style, positionAttributes.style);
    };

    if (!mounted && ref && adjustPositionAfterMillis > 0){
        setTimeout(() => {
            callback();
        }, adjustPositionAfterMillis);
        setMounted(true);
    }

    const boxClasses = classnames(
        classes.box,
        {
            [classes.boxHidden]: !isOpen,
            [classes.withAnimation]: withAnimation
        },
        className
    );
    const contentClasses = classnames(
        classes.content,
        contentClassName
    );

    const updateRef = (el) => {
        preventCloseAction(el);
        setRef(el);
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!ref) { return; }

        const observer = new MutationObserver(callback);

        observer.observe(ref, MUTATION_OBSERVER_CONFIG);

        isOpen && callback();

        const anchorScrollParent = getScrollParent(anchor);
        anchorScrollParent && anchorScrollParent.addEventListener('scroll', callback);

        window.addEventListener('resize', callback);

        if (appendToBody && trigger === TRIGGERS.HOVER) {
            window.addEventListener('scroll', callback);
        }

        return () => {
            observer.disconnect();
            anchorScrollParent && anchorScrollParent.removeEventListener('scroll', callback);
            window.removeEventListener('resize', callback);
            if (appendToBody && trigger === TRIGGERS.HOVER) {
                window.removeEventListener('scroll', callback);
            }
        };
    }, [ref, anchor, position, allowOverflow, fullWidth, isOpen]);

    if (!Object.values(POSITIONS).includes(position)) {
        throw new Error(UNSUPPORTED_POSITION_ERROR);
    }

    if (!container || !children) { return null; }

    return createPortal((
        <aside className={boxClasses}
            data-position={modifiedPosition}
            ref={updateRef}
            style={style}
            onMouseLeave={onMouseLeave}
            onMouseEnter={onMouseEnter}>
            <div className={contentClasses}>
                {getRenderable(children)}
            </div>
        </aside>
    ), container);
};

Box.propTypes = {
    children: PropTypes.node.isRequired,
    position: PropTypes.oneOf(Object.values(POSITIONS)).isRequired,
    container: PropTypes.object,
    isOpen: PropTypes.bool,
    style: PropTypes.object,
    anchor: PropTypes.object,
    className: PropTypes.string,
    contentClassName: PropTypes.string,
    allowOverflow: PropTypes.bool,
    fullWidth: PropTypes.bool,
    boxFitContent: PropTypes.bool,
    preventCloseAction: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onMouseEnter: PropTypes.func,
    appendToBody: PropTypes.bool,
    trigger: PropTypes.oneOf(Object.values(TRIGGERS)),
    withAnimation: PropTypes.bool.isRequired,
    adjustPositionAfterMillis: PropTypes.number
};

export default Box;
